"use client";

import { useEffect, useRef, type ReactNode } from "react";

import type { HomeServicesFoldVariant } from "./CcHomeServicesFold";

/**
 * Both variants keep a single active card, ignoring hover entries caused by
 * the layout changing beneath a stationary pointer. On
 * touch screens (no hover) the card closest to the middle of the screen
 * opens instead, so phones still see each scene while scrolling.
 */
export function CcServicesFoldGrid({ children, variant = "icon-only" }: { children: ReactNode; variant?: HomeServicesFoldVariant }) {
  const ref = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const list = ref.current;
    if (!list) return;
    const cards = [...list.children] as HTMLElement[];
    let hovered: HTMLElement | null = null;
    let focused: HTMLElement | null = null;
    let lastPosition: { x: number; y: number } | null = null;
    const cardAt = (target: EventTarget | null) => {
      const card = target instanceof Element
        ? target.closest<HTMLElement>(".cc-hsf-card")
        : null;
      return card && cards.includes(card) ? card : null;
    };
    const activate = (active: HTMLElement | null) => {
      for (const card of cards) {
        const value = String(card === active);
        if (card.dataset.active !== value) card.dataset.active = value;
      }
    };
    const move = (event: PointerEvent) => {
      if (event.pointerType === "touch" || !window.matchMedia("(hover: hover)").matches) return;
      // A resize may retarget hover at the same coordinates. Only genuine
      // pointer movement can hand control to a different card.
      if (lastPosition?.x === event.clientX && lastPosition.y === event.clientY) return;
      lastPosition = { x: event.clientX, y: event.clientY };
      const candidate = cardAt(event.target);
      if (!candidate) return; // Preserve the open card across the grid gaps.
      hovered = candidate;
      activate(hovered);
    };
    const leave = () => {
      hovered = null;
      lastPosition = null;
      activate(focused);
    };
    const focusIn = (event: FocusEvent) => {
      focused = cardAt(event.target);
      if (focused) activate(focused);
    };
    const focusOut = (event: FocusEvent) => {
      focused = cardAt(event.relatedTarget);
      activate(focused ?? hovered);
    };
    list.addEventListener("pointermove", move);
    list.addEventListener("pointerleave", leave);
    list.addEventListener("pointercancel", leave);
    list.addEventListener("focusin", focusIn);
    list.addEventListener("focusout", focusOut);
    return () => {
      list.removeEventListener("pointermove", move);
      list.removeEventListener("pointerleave", leave);
      list.removeEventListener("pointercancel", leave);
      list.removeEventListener("focusin", focusIn);
      list.removeEventListener("focusout", focusOut);
    };
  }, [variant]);

  useEffect(() => {
    const list = ref.current;
    if (!list || variant !== "visible-illustration") return;
    // Measure the actual narrow slot, including neighbouring cards as one opens.
    // Only scale the fixed scene UI; its text never reflows during the reveal.
    const media = [...list.querySelectorAll<HTMLElement>(".cc-hsf-card__media")];
    const fit = (node: HTMLElement, width: number) => {
      node.style.setProperty("--cc-hsf-compact-scale", Math.min(1, Math.max(0.2, (width - 16) / 325)).toFixed(4));
    };
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) fit(entry.target as HTMLElement, entry.contentRect.width);
    });
    for (const node of media) {
      fit(node, node.clientWidth);
      observer.observe(node);
    }
    return () => observer.disconnect();
  }, [variant]);

  useEffect(() => {
    const list = ref.current;
    if (!list || window.matchMedia("(hover: hover)").matches) return;
    const cards = Array.from(list.children) as HTMLElement[];
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          (entry.target as HTMLElement).dataset.active = String(
            entry.isIntersecting,
          );
        }
      },
      { rootMargin: "-40% 0px -40% 0px" },
    );
    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, [variant]);

  return (
    <ul ref={ref} className="cc-hsf__grid">
      {children}
    </ul>
  );
}
